import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Car, Building2, Folder, ClipboardList, CalendarDays, Ruler, type LucideIcon } from 'lucide-react';
import {
  installCalendarCreateGestureSwallow,
  isCalendarCreateTarget,
  swallowCalendarCreateEvent,
} from '../hooks/useDismissOverlayProtectingCalendarCreate';

export type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

interface BookingTypeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMobileBooking: () => void;
  onSelectLocationBooking: () => void;
  onSelectConvertProject: () => void;
  onSelectTask?: () => void;
  onSelectFieldQuote?: () => void;
  onSelectGoogleCalendarEvent?: () => void;
  showMobileBooking: boolean;
  showLocationBooking: boolean;
  showConvertProject: boolean;
  showTask?: boolean;
  showFieldQuote?: boolean;
  showGoogleCalendarEvent?: boolean;
  selectedResourceName?: string;
  selectedTimeSlot?: string;
  selectedDate?: string;
  /** Live slot datetime — enables an editable time field in the header. */
  slotDate?: Date | null;
  onSlotDateChange?: (date: Date) => void;
  /** Optional exclusive end of a drag-selected range. */
  slotEndDate?: Date | null;
  onSlotEndDateChange?: (date: Date) => void;
  /** When provided, menu anchors beside this slot instead of centering. */
  anchorRect?: AnchorRect | null;
  /**
   * Live DOM anchor so the popover can re-measure on scroll.
   * Week slots: `weekSlotKey(...)` → `[data-calendar-create-slot="..."]`
   * Month days: `month:yyyy-MM-dd` → FullCalendar `[data-date="..."]`
   */
  anchorSlotKey?: string | null;
  /** Optional end of a multi-slot/day selection — used to keep the full range highlight. */
  anchorEndSlotKey?: string | null;
}

interface CreateOption {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  onSelect: () => void;
}

type Placement = {
  top: number;
  left: number;
  caretSide: 'left' | 'right' | 'top';
  transformOrigin: string;
};

const MENU_WIDTH = 300;
const MENU_GAP = 10;
const VIEWPORT_PAD = 12;
const ESTIMATED_MENU_HEIGHT = 280;

export function monthCreateSlotKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `month:${y}-${m}-${d}`;
}

function resolveAnchorElement(slotKey: string | null | undefined): HTMLElement | null {
  if (!slotKey) return null;
  if (slotKey.startsWith('month:')) {
    const date = slotKey.slice(6);
    return (
      document.querySelector<HTMLElement>(`.fc-daygrid-day[data-date="${date}"]`) ||
      document.querySelector<HTMLElement>(`[data-date="${date}"]`)
    );
  }
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(slotKey)
      : slotKey.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return document.querySelector<HTMLElement>(
    `[data-calendar-create-slot="${escaped}"]`
  );
}

function unionRects(a: AnchorRect, b: AnchorRect): AnchorRect {
  const top = Math.min(a.top, b.top);
  const left = Math.min(a.left, b.left);
  const right = Math.max(a.left + a.width, b.left + b.width);
  const bottom = Math.max(a.top + a.height, b.top + b.height);
  return { top, left, width: right - left, height: bottom - top };
}

function resolveRangeRect(
  startKey: string | null | undefined,
  endKey: string | null | undefined,
  fallback: AnchorRect | null | undefined
): AnchorRect | null {
  const startEl = resolveAnchorElement(startKey);
  const endEl = endKey ? resolveAnchorElement(endKey) : null;

  // Month multi-day: never build a bounding-box union (it fills empty grid holes).
  // Anchor the popover to the start day; FullCalendar keeps the real cell highlights.
  if (startKey?.startsWith('month:') && endKey && endKey !== startKey) {
    if (startEl) return rectFromElement(startEl);
    return fallback ?? null;
  }

  if (startEl && endEl) {
    return unionRects(rectFromElement(startEl), rectFromElement(endEl));
  }
  if (startEl) return rectFromElement(startEl);
  if (endEl) return rectFromElement(endEl);
  return fallback ?? null;
}

function rectFromElement(el: HTMLElement): AnchorRect {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

/** Bottom edge of fixed calendar chrome (nav + calendar header + sticky grid header). */
function getCalendarChromeBottom(): number {
  let bottom = 0;

  const fixedHeader = document.querySelector(
    '.calendar-fixed-header'
  ) as HTMLElement | null;
  if (fixedHeader) {
    bottom = Math.max(bottom, fixedHeader.getBoundingClientRect().bottom);
  }

  const gridHeader = document.querySelector(
    '[data-calendar-grid-header]'
  ) as HTMLElement | null;
  if (gridHeader) {
    const rect = gridHeader.getBoundingClientRect();
    // Only count it when it's actually sticking near the top chrome
    if (rect.bottom > bottom && rect.top < bottom + 8) {
      bottom = Math.max(bottom, rect.bottom);
    } else if (!fixedHeader) {
      bottom = Math.max(bottom, rect.bottom);
    }
  }

  if (bottom > 0) return bottom;

  const nav =
    (document.querySelector('[data-app-navbar]') as HTMLElement | null) ||
    (document.querySelector('nav.fixed, header.fixed') as HTMLElement | null);
  if (nav) {
    return nav.getBoundingClientRect().bottom;
  }

  // Fallback: ~top nav (3rem) + calendar toolbar
  return 48 + 72;
}

/**
 * Clip a slot rect so it never paints over the fixed calendar header / top nav.
 * Returns null when almost nothing remains below the chrome.
 */
function clipRectBelowChrome(rect: AnchorRect, chromeBottom: number): AnchorRect | null {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top = Math.max(rect.top, chromeBottom + 2);
  const bottom = Math.min(rect.top + rect.height, vh - 4);
  const left = Math.max(rect.left, 0);
  const right = Math.min(rect.left + rect.width, vw);
  const height = bottom - top;
  const width = right - left;
  if (height < 10 || width < 8) return null;
  return { top, left, width, height };
}

function computePlacement(
  anchor: AnchorRect,
  menuHeight: number,
  chromeBottom = 0
): Placement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const minTop = Math.max(VIEWPORT_PAD, chromeBottom + 8);
  const height = Math.min(menuHeight || ESTIMATED_MENU_HEIGHT, vh - minTop - VIEWPORT_PAD);

  const spaceRight = vw - (anchor.left + anchor.width) - VIEWPORT_PAD;
  const spaceLeft = anchor.left - VIEWPORT_PAD;

  let caretSide: Placement['caretSide'] = 'left';
  let left: number;
  let transformOrigin: string;

  if (spaceRight >= MENU_WIDTH + MENU_GAP || spaceRight >= spaceLeft) {
    left = anchor.left + anchor.width + MENU_GAP;
    caretSide = 'left';
    transformOrigin = 'left center';
    if (left + MENU_WIDTH > vw - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, vw - VIEWPORT_PAD - MENU_WIDTH);
    }
  } else if (spaceLeft >= MENU_WIDTH + MENU_GAP) {
    left = anchor.left - MENU_GAP - MENU_WIDTH;
    caretSide = 'right';
    transformOrigin = 'right center';
  } else {
    left = anchor.left + anchor.width / 2 - MENU_WIDTH / 2;
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - VIEWPORT_PAD - MENU_WIDTH));
    caretSide = 'top';
    transformOrigin = 'top center';
  }

  let top: number;
  if (caretSide === 'top') {
    top = anchor.top + anchor.height + MENU_GAP;
    if (top + height > vh - VIEWPORT_PAD) {
      top = Math.max(minTop, anchor.top - MENU_GAP - height);
      transformOrigin = 'bottom center';
    }
  } else {
    top = anchor.top + anchor.height / 2 - height / 2;
    top = Math.max(minTop, Math.min(top, vh - VIEWPORT_PAD - height));
  }

  // Final clamp — never cover the calendar header / nav
  top = Math.max(minTop, top);

  return { top, left, caretSide, transformOrigin };
}

export default function BookingTypeSelectionModal({
  isOpen,
  onClose,
  onSelectMobileBooking,
  onSelectLocationBooking,
  onSelectConvertProject,
  onSelectTask,
  onSelectFieldQuote,
  onSelectGoogleCalendarEvent,
  showMobileBooking,
  showLocationBooking,
  showConvertProject,
  showTask = false,
  showFieldQuote = false,
  showGoogleCalendarEvent = false,
  selectedResourceName,
  selectedTimeSlot,
  selectedDate,
  slotDate = null,
  onSlotDateChange,
  slotEndDate = null,
  onSlotEndDateChange,
  anchorRect = null,
  anchorSlotKey = null,
  anchorEndSlotKey = null,
}: BookingTypeSelectionModalProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const endTimeInputRef = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [liveRect, setLiveRect] = useState<AnchorRect | null>(anchorRect);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [menuHeight, setMenuHeight] = useState(ESTIMATED_MENU_HEIGHT);

  const anchored = Boolean(
    liveRect && liveRect.width > 0 && liveRect.height > 0
  );

  // Keep liveRect in sync when parent passes a fresh click rect / key
  useEffect(() => {
    if (!isOpen) {
      setLiveRect(null);
      return;
    }
    const chromeBottom = getCalendarChromeBottom();
    const raw =
      resolveRangeRect(anchorSlotKey, anchorEndSlotKey, anchorRect) ||
      anchorRect ||
      null;
    setLiveRect(raw ? clipRectBelowChrome(raw, chromeBottom) : null);
  }, [isOpen, anchorRect, anchorSlotKey, anchorEndSlotKey]);

  useEffect(() => {
    if (!isOpen || anchored) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, anchored]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const syncToSlot = useCallback(() => {
    if (!anchorSlotKey && !anchorRect) {
      setLiveRect(null);
      setPlacement(null);
      return;
    }

    const chromeBottom = getCalendarChromeBottom();
    const raw = resolveRangeRect(anchorSlotKey, anchorEndSlotKey, anchorRect);

    if (!raw) {
      // Start element gone — close; don't collapse a range to a missing single cell
      if (anchorSlotKey && !resolveAnchorElement(anchorSlotKey)) {
        onCloseRef.current();
      }
      return;
    }

    const clipped = clipRectBelowChrome(raw, chromeBottom);
    if (!clipped) {
      onCloseRef.current();
      return;
    }

    setLiveRect(clipped);
    setPlacement(computePlacement(clipped, menuHeight, chromeBottom));
  }, [anchorSlotKey, anchorEndSlotKey, anchorRect, menuHeight]);

  useLayoutEffect(() => {
    if (!isOpen || !liveRect) return;
    setPlacement(computePlacement(liveRect, menuHeight, getCalendarChromeBottom()));
  }, [isOpen, liveRect, menuHeight]);

  useLayoutEffect(() => {
    if (!isOpen || !anchored || !menuRef.current) return;
    const h = menuRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - menuHeight) > 2) {
      setMenuHeight(h);
    }
  }, [isOpen, anchored, menuHeight, selectedDate, selectedTimeSlot, selectedResourceName]);

  // Re-anchor on scroll/resize so the menu stays glued to the slot (or closes if it leaves view)
  useEffect(() => {
    if (!isOpen || !anchored) return;

    let raf = 0;
    const scheduleSync = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        syncToSlot();
      });
    };

    window.addEventListener('scroll', scheduleSync, true);
    window.addEventListener('resize', scheduleSync);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', scheduleSync, true);
      window.removeEventListener('resize', scheduleSync);
    };
  }, [isOpen, anchored, syncToSlot]);

  // Allow scrolling the calendar under a soft veil; dismiss on outside click.
  // First outside click must ONLY close the picker — never open a new slot under the cursor.
  useEffect(() => {
    if (!isOpen || !anchored) return;

    let dismissed = false;

    const onOutsideDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;

      const onSlot = isCalendarCreateTarget(e.target);

      // pointerdown + mousedown both fire for mouse — swallow both, close once
      if (dismissed) {
        if (onSlot) swallowCalendarCreateEvent(e);
        return;
      }

      dismissed = true;
      onCloseRef.current();

      // Only block the gesture when it would re-trigger a slot/day under the picker.
      // Clicks on chrome (view switcher, nav) can still proceed after close.
      if (onSlot) {
        swallowCalendarCreateEvent(e);
        installCalendarCreateGestureSwallow();
      }
    };

    document.addEventListener('pointerdown', onOutsideDown, true);
    document.addEventListener('mousedown', onOutsideDown, true);
    document.addEventListener('touchstart', onOutsideDown, true);

    return () => {
      dismissed = true;
      document.removeEventListener('pointerdown', onOutsideDown, true);
      document.removeEventListener('mousedown', onOutsideDown, true);
      document.removeEventListener('touchstart', onOutsideDown, true);
    };
  }, [isOpen, anchored]);

  const options: CreateOption[] = useMemo(
    () =>
      [
        showMobileBooking && {
          id: 'mobile-booking',
          label: 'Mobile Booking',
          description: 'Create a mobile service booking',
          icon: Car,
          onSelect: onSelectMobileBooking,
        },
        showLocationBooking && {
          id: 'location-booking',
          label: 'Location Booking',
          description: 'Create a location-based booking',
          icon: Building2,
          onSelect: onSelectLocationBooking,
        },
        showConvertProject && {
          id: 'convert-project',
          label: 'Convert Project to Booking',
          description: 'Convert an existing project to a booking',
          icon: Folder,
          onSelect: onSelectConvertProject,
        },
        showTask &&
          onSelectTask && {
            id: 'task',
            label: 'Create Calendar Event',
            description: 'Create a calendar event for this time slot',
            icon: ClipboardList,
            onSelect: onSelectTask,
          },
        showFieldQuote &&
          onSelectFieldQuote && {
            id: 'field-quote',
            label: 'Field Quote',
            description: 'Schedule an on-site field quote',
            icon: Ruler,
            onSelect: onSelectFieldQuote,
          },
        showGoogleCalendarEvent &&
          onSelectGoogleCalendarEvent && {
            id: 'gcal-event',
            label: 'Google Calendar Event',
            description: 'Create a meeting and invite people',
            icon: CalendarDays,
            onSelect: onSelectGoogleCalendarEvent,
          },
      ].filter(Boolean) as CreateOption[],
    [
      showMobileBooking,
      showLocationBooking,
      showConvertProject,
      showTask,
      showFieldQuote,
      showGoogleCalendarEvent,
      onSelectMobileBooking,
      onSelectLocationBooking,
      onSelectConvertProject,
      onSelectTask,
      onSelectFieldQuote,
      onSelectGoogleCalendarEvent,
    ]
  );

  if (!isOpen) return null;

  const formatDayLabel = (date: Date) =>
    date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

  const hasRange =
    !!slotDate &&
    !!slotEndDate &&
    slotEndDate.getTime() > slotDate.getTime();
  const startDateLabel = slotDate ? formatDayLabel(slotDate) : selectedDate;
  const endDateLabel =
    hasRange && slotEndDate ? formatDayLabel(slotEndDate) : null;
  const timeValue = slotDate
    ? `${String(slotDate.getHours()).padStart(2, '0')}:${String(slotDate.getMinutes()).padStart(2, '0')}`
    : null;
  const timeDisplay = slotDate
    ? slotDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : selectedTimeSlot;
  const endTimeValue = hasRange && slotEndDate
    ? `${String(slotEndDate.getHours()).padStart(2, '0')}:${String(slotEndDate.getMinutes()).padStart(2, '0')}`
    : null;
  const endTimeDisplay = hasRange && slotEndDate
    ? slotEndDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  const staticContextLine = [selectedDate, selectedTimeSlot].filter(Boolean).join(' · ');

  const handleTimeChange = (value: string) => {
    if (!slotDate || !onSlotDateChange || !value) return;
    const [hoursStr, minutesStr] = value.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
    const next = new Date(slotDate);
    next.setHours(hours, minutes, 0, 0);
    onSlotDateChange(next);
  };

  const handleEndTimeChange = (value: string) => {
    if (!onSlotEndDateChange || !value) return;
    const base = slotEndDate
      ? new Date(slotEndDate)
      : slotDate
        ? new Date(slotDate)
        : null;
    if (!base) return;
    const [hoursStr, minutesStr] = value.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
    base.setHours(hours, minutes, 0, 0);
    onSlotEndDateChange(base);
  };

  const openNativeTimePicker = (
    e: React.SyntheticEvent,
    which: 'start' | 'end' = 'start'
  ) => {
    e.stopPropagation();
    const input = which === 'end' ? endTimeInputRef.current : timeInputRef.current;
    if (!input) return;
    input.focus();
    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
      }
    } catch {
      // Some browsers only allow showPicker from a direct user gesture; focus still helps.
    }
  };

  const menuShellClass =
    'ios-create-popover w-[300px] max-w-[calc(100vw-24px)] overflow-hidden pointer-events-auto animate-create-popover-in';

  const menuShellStyle: React.CSSProperties = {
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.78)',
    border: '0.5px solid rgba(0, 0, 0, 0.08)',
    boxShadow:
      '0 0 0 0.5px rgba(0,0,0,0.04), 0 10px 40px rgba(0,0,0,0.18), 0 2px 10px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
  };

  const menuInner = (
    <>
      {/* iOS grouped header */}
      <div className="flex items-center gap-2 px-3.5 pt-3 pb-2.5">
        <div className="min-w-0 flex-1">
          <p
            className="text-[11px] font-semibold tracking-wide text-black/35"
            style={{ letterSpacing: '0.02em' }}
          >
            Create
          </p>
          {slotDate && onSlotDateChange ? (
            <div className="mt-1.5 space-y-1.5">
              {selectedResourceName ? (
                <p className="text-[12px] text-black/45 font-medium truncate">
                  {selectedResourceName}
                </p>
              ) : null}
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="min-w-0 flex items-baseline gap-1.5">
                  {hasRange ? (
                    <span className="text-[11px] font-semibold text-black/35 shrink-0 w-9">
                      Start
                    </span>
                  ) : null}
                  <span className="text-[13px] text-black/70 font-medium whitespace-nowrap">
                    {startDateLabel}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => openNativeTimePicker(e, 'start')}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="relative inline-flex items-center shrink-0 rounded-full px-2.5 py-1 text-[13px] font-semibold text-[#007AFF] bg-[#007AFF]/[0.10] hover:bg-[#007AFF]/[0.16] active:bg-[#007AFF]/[0.22] transition-colors cursor-pointer"
                  aria-label="Change start time"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {timeDisplay}
                  <input
                    ref={timeInputRef}
                    type="time"
                    value={timeValue ?? ''}
                    step={900}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    className="absolute inset-0 opacity-0 pointer-events-none"
                    tabIndex={-1}
                  />
                </button>
              </div>
              {hasRange && onSlotEndDateChange ? (
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="min-w-0 flex items-baseline gap-1.5">
                    <span className="text-[11px] font-semibold text-black/35 shrink-0 w-9">
                      End
                    </span>
                    <span className="text-[13px] text-black/70 font-medium whitespace-nowrap">
                      {endDateLabel}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => openNativeTimePicker(e, 'end')}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="relative inline-flex items-center shrink-0 rounded-full px-2.5 py-1 text-[13px] font-semibold text-[#007AFF] bg-[#007AFF]/[0.10] hover:bg-[#007AFF]/[0.16] active:bg-[#007AFF]/[0.22] transition-colors cursor-pointer"
                    aria-label="Change end time"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {endTimeDisplay}
                    <input
                      ref={endTimeInputRef}
                      type="time"
                      value={endTimeValue ?? ''}
                      step={900}
                      onChange={(e) => handleEndTimeChange(e.target.value)}
                      className="absolute inset-0 opacity-0 pointer-events-none"
                      tabIndex={-1}
                    />
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            (staticContextLine || selectedResourceName) && (
              <p className="mt-1 text-[13px] text-black/55 truncate font-medium">
                {staticContextLine}
                {selectedResourceName
                  ? `${staticContextLine ? ' · ' : ''}${selectedResourceName}`
                  : ''}
              </p>
            )
          )}
        </div>
        <button
          onClick={onClose}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-black/40 hover:bg-black/[0.1] active:bg-black/[0.14] transition-colors"
          aria-label="Close"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>

      <div className="mx-2.5 mb-2.5 overflow-hidden rounded-[10px] bg-black/[0.035]">
        {options.map((option, index) => {
          const Icon = option.icon;
          const isLast = index === options.length - 1;
          return (
            <button
              key={option.id}
              type="button"
              role="menuitem"
              onClick={() => {
                option.onSelect();
                onClose();
              }}
              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left touch-manipulation transition-colors hover:bg-black/[0.04] active:bg-black/[0.08] animate-create-option-in ${
                !isLast ? 'border-b border-black/[0.06]' : ''
              }`}
              style={{
                WebkitTapHighlightColor: 'transparent',
                animationDelay: `${50 + index * 40}ms`,
              }}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[#007AFF] text-white shadow-sm shadow-[#007AFF]/30">
                <Icon className="h-[15px] w-[15px]" strokeWidth={2.25} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-black/85 leading-tight">
                  {option.label}
                </span>
                <span className="block text-[12px] text-black/40 mt-0.5 leading-snug">
                  {option.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  if (anchored && liveRect) {
    const chromeBottom = getCalendarChromeBottom();
    const pos = placement ?? computePlacement(liveRect, menuHeight, chromeBottom);
    // Month multi-day: FullCalendar already paints each selected day. A floating
    // bounding-box ghost would cover empty grid holes between week rows.
    const hideFloatingGhost = Boolean(
      anchorSlotKey?.startsWith('month:') &&
        anchorEndSlotKey &&
        anchorEndSlotKey !== anchorSlotKey
    );

    return createPortal(
      <>
        {/* Visual veil only below calendar chrome — pointer-events none so calendar can scroll */}
        <div
          className="fixed animate-create-backdrop-in pointer-events-none"
          style={{
            zIndex: 40,
            top: chromeBottom,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.06)',
          }}
        />

        {/* Slot ghost — week ranges / single cells only */}
        {!hideFloatingGhost ? (
          <div
            className="fixed pointer-events-none animate-create-slot-in"
            style={{
              zIndex: 40,
              top: liveRect.top,
              left: liveRect.left,
              width: Math.max(liveRect.width, 2),
              height: Math.max(liveRect.height, 2),
              borderRadius: 6,
              background: 'rgba(59, 130, 246, 0.16)',
              border: '1.5px dashed rgba(59, 130, 246, 0.65)',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)',
            }}
          />
        ) : null}

        <div
          ref={menuRef}
          role="menu"
          className={`fixed ${menuShellClass}`}
          style={{
            ...menuShellStyle,
            zIndex: 100160,
            top: pos.top,
            left: pos.left,
            maxHeight: `min(90vh, ${Math.max(120, window.innerHeight - pos.top - 12)}px)`,
            overflowY: 'auto',
            transformOrigin: pos.transformOrigin,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {pos.caretSide === 'left' && (
            <span
              aria-hidden
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                left: -5,
                top: Math.min(
                  Math.max(liveRect.top + liveRect.height / 2 - pos.top - 5, 16),
                  menuHeight - 22
                ),
                background: 'rgba(255, 255, 255, 0.86)',
                borderLeft: '0.5px solid rgba(0,0,0,0.08)',
                borderBottom: '0.5px solid rgba(0,0,0,0.08)',
              }}
            />
          )}
          {pos.caretSide === 'right' && (
            <span
              aria-hidden
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                right: -5,
                top: Math.min(
                  Math.max(liveRect.top + liveRect.height / 2 - pos.top - 5, 16),
                  menuHeight - 22
                ),
                background: 'rgba(255, 255, 255, 0.86)',
                borderRight: '0.5px solid rgba(0,0,0,0.08)',
                borderTop: '0.5px solid rgba(0,0,0,0.08)',
              }}
            />
          )}
          {pos.caretSide === 'top' && (
            <span
              aria-hidden
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                top: -5,
                left: Math.min(
                  Math.max(liveRect.left + liveRect.width / 2 - pos.left - 5, 16),
                  MENU_WIDTH - 24
                ),
                background: 'rgba(255, 255, 255, 0.86)',
                borderLeft: '0.5px solid rgba(0,0,0,0.08)',
                borderTop: '0.5px solid rgba(0,0,0,0.08)',
              }}
            />
          )}
          {menuInner}
        </div>
      </>,
      document.body
    );
  }

  // Centered fallback (FAB / ProjectInfoModal / no geometry)
  return createPortal(
    <>
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100150,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />

      <div
        className="fixed inset-0 flex items-center justify-center pointer-events-none px-4"
        style={{ zIndex: 100160 }}
      >
        <div
          role="menu"
          className={menuShellClass}
          style={{
            ...menuShellStyle,
            maxHeight: '90vh',
            overflowY: 'auto',
            transformOrigin: 'center center',
            width: '100%',
            maxWidth: 300,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {menuInner}
        </div>
      </div>
    </>,
    document.body
  );
}
